import { ThemeOptions } from '@material-ui/core/styles';

type SpriveThemeOptions = ThemeOptions & {
    contentWithSidebar?: {
        marginTop?: number;
    };
    content?: {
        marginTop?: number;
    };
};

export const darkTheme: SpriveThemeOptions = {
    palette: {
        type: 'dark',
        primary: {
            main: '#8ccbd0',
        },
        secondary: {
            main: '#d9a066',
        },
        background: {
            default: '#121517',
            paper: '#181c1f',
        },
    },
    shape: {
        borderRadius: 14,
    },
    typography: {
        fontFamily: '"Manrope", "Space Grotesk", "Segoe UI", sans-serif',
        h1: { fontFamily: '"Space Grotesk", "Manrope", sans-serif', fontWeight: 700 },
        h2: { fontFamily: '"Space Grotesk", "Manrope", sans-serif', fontWeight: 700 },
        h3: { fontFamily: '"Space Grotesk", "Manrope", sans-serif', fontWeight: 700 },
        h4: { fontFamily: '"Space Grotesk", "Manrope", sans-serif', fontWeight: 700 },
        h5: { fontFamily: '"Space Grotesk", "Manrope", sans-serif', fontWeight: 700 },
        h6: { fontFamily: '"Space Grotesk", "Manrope", sans-serif', fontWeight: 700 },
        button: { textTransform: 'none', fontWeight: 600 },
    },
};

export const lightTheme: SpriveThemeOptions = {
    palette: {
        type: 'light',
        primary: {
            main: '#1f4d55',
            light: '#2f6c73',
            dark: '#12343a',
            contrastText: '#fdfbf7',
        },
        secondary: {
            main: '#c27a38',
            light: '#e0a66e',
            dark: '#8f5522',
            contrastText: '#1f1308',
        },
        background: {
            default: '#f4f1eb',
            paper: '#ffffff',
        },
        text: {
            primary: '#1c1e1f',
            secondary: '#4f5b60',
        },
    },
    shape: {
        borderRadius: 14,
    },
    typography: {
        fontFamily: '"Manrope", "Space Grotesk", "Segoe UI", sans-serif',
        h1: { fontFamily: '"Space Grotesk", "Manrope", sans-serif', fontWeight: 700 },
        h2: { fontFamily: '"Space Grotesk", "Manrope", sans-serif', fontWeight: 700 },
        h3: { fontFamily: '"Space Grotesk", "Manrope", sans-serif', fontWeight: 700 },
        h4: { fontFamily: '"Space Grotesk", "Manrope", sans-serif', fontWeight: 700 },
        h5: { fontFamily: '"Space Grotesk", "Manrope", sans-serif', fontWeight: 700 },
        h6: { fontFamily: '"Space Grotesk", "Manrope", sans-serif', fontWeight: 700 },
        subtitle1: { fontWeight: 600 },
        button: { textTransform: 'none', fontWeight: 600 },
    },
    contentWithSidebar: {
        marginTop: 32,
    },
    content: {
        marginTop: 32,
    },
    overrides: ({
        MuiCssBaseline: {
            '@global': {
                body: {
                    backgroundColor: '#f4f1eb',
                    backgroundImage:
                        'radial-gradient(circle at 10% 10%, rgba(195, 200, 188, 0.35), transparent 45%), radial-gradient(circle at 90% 0%, rgba(207, 178, 146, 0.35), transparent 40%)',
                    color: '#1c1e1f',
                },
                '#root': {
                    minHeight: '100vh',
                },
            },
        },
        RaMenuItemLink: {
            root: {
                borderRadius: 12,
                margin: '6px 12px',
                padding: '8px 12px',
                borderLeft: '3px solid transparent',
                transition: 'all 150ms ease',
                color: '#3f4a4e',
                alignItems: 'flex-start',
                '& .MuiListItemText-root': {
                    margin: 0,
                },
                '& .MuiListItemText-primary': {
                    lineHeight: 1.35,
                },
                '& .MuiListItemIcon-root': {
                    color: '#5f6f73',
                    marginTop: 2,
                },
            },
            active: {
                borderLeft: '3px solid #1f4d55',
                backgroundColor: 'rgba(31, 77, 85, 0.12)',
                color: '#1f4d55',
            },
        },
        MuiPaper: {
            elevation1: {
                boxShadow: '0 8px 24px rgba(28, 30, 31, 0.08)',
            },
            root: {
                border: '1px solid rgba(28, 30, 31, 0.08)',
                backgroundClip: 'padding-box',
            },
        },
        MuiCard: {
            root: {
                borderRadius: 18,
            },
        },
        MuiAppBar: {
            colorSecondary: {
                color: '#1c1e1f',
                backgroundColor: 'rgba(255, 255, 255, 0.92)',
                backdropFilter: 'blur(12px)',
                borderBottom: '1px solid rgba(28, 30, 31, 0.08)',
            },
        },
        MuiToolbar: {
            regular: {
                minHeight: 72,
            },
        },
        MuiDrawer: {
            paper: {
                backgroundColor: '#f1ede6',
                borderRight: '1px solid rgba(28, 30, 31, 0.08)',
            },
        },
        MuiListItemIcon: {
            root: {
                minWidth: 40,
                color: '#5f6f73',
            },
        },
        MuiButton: {
            contained: {
                backgroundColor: '#1f4d55',
                color: '#fdfbf7',
                boxShadow: '0 10px 22px rgba(31, 77, 85, 0.2)',
            },
            containedSecondary: {
                backgroundColor: '#c27a38',
                color: '#1f1308',
            },
            outlined: {
                borderColor: 'rgba(31, 77, 85, 0.4)',
            },
        },
        MuiInputBase: {
            root: {
                borderRadius: 12,
            },
        },
        MuiOutlinedInput: {
            root: {
                borderRadius: 12,
                backgroundColor: '#ffffff',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1f4d55',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1f4d55',
                    borderWidth: 2,
                },
            },
        },
        MuiFormLabel: {
            root: {
                maxWidth: 'calc(100% - 24px)',
                whiteSpace: 'normal',
                lineHeight: 1.2,
            },
        },
        MuiInputLabel: {
            root: {
                maxWidth: 'calc(100% - 24px)',
                whiteSpace: 'normal',
                lineHeight: 1.2,
            },
            shrink: {
                backgroundColor: '#f4f1eb',
                padding: '0 6px',
            },
        },
        MuiFilledInput: {
            root: {
                backgroundColor: 'rgba(31, 77, 85, 0.06)',
                borderRadius: 12,
                '&$disabled': {
                    backgroundColor: 'rgba(31, 77, 85, 0.06)',
                },
            },
        },
        MuiTableCell: {
            head: {
                backgroundColor: 'rgba(31, 77, 85, 0.06)',
                color: '#1f4d55',
                fontWeight: 700,
                textTransform: 'uppercase' as const,
                letterSpacing: '0.04em',
                fontSize: '0.72rem',
            },
            body: {
                borderBottom: '1px solid rgba(28, 30, 31, 0.08)',
            },
        },
        MuiTableRow: {
            hover: {
                backgroundColor: 'rgba(31, 77, 85, 0.04)',
            },
        },
        MuiTabs: {
            indicator: {
                height: 3,
                borderRadius: 999,
            },
        },
        MuiTab: {
            root: {
                textTransform: 'none' as const,
                fontWeight: 600,
            },
        },
        MuiLinearProgress: {
            colorPrimary: {
                backgroundColor: '#e6e1db',
            },
            barColorPrimary: {
                backgroundColor: '#aab6b0',
            },
        },
        MuiChip: {
            root: {
                borderRadius: 999,
                fontWeight: 600,
            },
        },
    } as any),
};
